import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import InputField from "../shared-components/InputField";
import validator from "validator";
import { NameType } from "../api/Person";
import { AliasesDefinitions } from "../persons/PersonFormAliases";
import { classNames, uniqueUUID } from "../utils";
import AddButton from "../shared-components/AddButton";
import { XIcon } from "../shared-components/icons";
import { ButtonColorTypes, ButtonTypes } from "../shared-components/Button";
import Button from "../shared-components/Button";
import {
  PersonInfoData,
  REPORT_WIZARD_STEPS,
  useReportsWizard,
} from "./ReportsWizardProvider";
import {
  isWizardNameWithinDbLimit,
  isWizardProfileUrlValid,
  sanitizeAlias,
} from "./ReportsWizardUtil";
import {
  cleanedProfileUrl,
  collectProfileUrls,
  PROFILE_URL_ERROR,
  profileUrlErrorMessage,
} from "../utils/personFieldLimits";
import { useParseResumeFromS3 } from "../api/PersonApi";
import { useFamaFlags } from "../api/FamaFlags";
import {
  useGetPersonDocumentUploadUrl,
  useUploadFileToS3,
} from "../api/360FilesAPI";
import { Resume } from "../api/Resume";

function isValidEmailValidationFunction(
  item: { key: string; email: string },
  idx: number
) {
  if (item.email.length === 0) {
    return false;
  }
  if (item.email.length > 0 && !validator.isEmail(item.email)) {
    return false;
  }
  return true;
}

function isValidUrlValidationFunction(
  item: { key: string; url: string },
  idx: number
) {
  return isWizardProfileUrlValid(item.url, idx);
}

function isValidPhoneValidationFunction(
  item: { key: string; phone: string },
  idx: number
) {
  if (item?.phone?.length === 0) {
    return true;
  }
  // the first items is allowed to be empty
  if (item.phone.length === 0 && idx === 0) {
    return true;
  }
  // the other items are required if added
  if (item.phone.length === 0 && idx > 0) {
    return false;
  }
  return true;
}

type Props = {};
function buildEmptyDefinition(isMain: boolean = false): AliasesDefinitions {
  return {
    firstName: "",
    middleName: "",
    lastName: "",
    key: uniqueUUID(),
    name_type: isMain ? NameType.MAIN : NameType.ALIAS,
  };
}
export const ReportsWizardPersonInfo = (props: Props) => {
  const {
    nextStep,
    getPersonInfo,
    setPersonInfo,
    steps,
    setIsDirty,
    getIsDirty,
    resumeToSteps,
    ensurePersonAvailable,
  } = useReportsWizard();
  const [isValid, setIsValid] = useState(false);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const { showResumeParse } = useFamaFlags();
  const {
    mutateAsync: getPersonDocumentUploadUrl,
    isError: getPersonDocumentUploadUrlError,
  } = useGetPersonDocumentUploadUrl();
  const { mutateAsync: uploadFileToS3, isError: uploadFileToS3Error } =
    useUploadFileToS3();
  const { mutateAsync: parseResumeFromS3, isError: parseResumeFromS3Error } =
    useParseResumeFromS3();
  const fileInput = useRef<HTMLInputElement>(null);
  const [tempFile, setTempFile] = useState<File | null>(null);
  const [fieldsList, setFields] = useState<AliasesDefinitions[]>(() => {
    const personInfo = getPersonInfo();
    return initializeFields(personInfo).aliases;
  });

  const anyResumeError = useMemo(() => {
    return (
      getPersonDocumentUploadUrlError ||
      uploadFileToS3Error ||
      parseResumeFromS3Error
    );
  }, [
    getPersonDocumentUploadUrlError,
    uploadFileToS3Error,
    parseResumeFromS3Error,
  ]);

  function initializeFields(personInfo: PersonInfoData | null): {
    aliases: AliasesDefinitions[];
    emails: { key: string; email: string }[];
    phones: { key: string; phone: string }[];
    urls: { key: string; url: string }[];
  } {
    if (personInfo) {
      return {
        aliases: personInfo.aliases.map((alias) => ({
          ...alias,
          key: uniqueUUID(),
          name_type: NameType.ALIAS,
        })),
        emails: personInfo.emails.map((email) => ({
          key: uniqueUUID(),
          email,
        })),
        phones: personInfo.phones.map((phone) => ({
          key: uniqueUUID(),
          phone,
        })),
        urls: personInfo.urls.map((url) => ({
          key: uniqueUUID(),
          url,
        })),
      };
    }
    return {
      aliases: [],
      emails: [],
      phones: [],
      urls: [],
    };
  }

  const initializeFieldsCallback = useCallback(
    (
      personInfo: PersonInfoData | null
    ): {
      aliases: AliasesDefinitions[];
      emails: { key: string; email: string }[];
      phones: { key: string; phone: string }[];
      urls: { key: string; url: string }[];
    } => {
      return initializeFields(personInfo);
    },
    []
  );

  const [birthDate, setBirthDate] = useState<string>(() => {
    const personInfo = getPersonInfo();
    if (personInfo) {
      return personInfo.dateOfBirth;
    }
    return "";
  });
  const [emails, setEmails] = useState<{ key: string; email: string }[]>(() => {
    const personInfo = getPersonInfo();
    if (personInfo) {
      return personInfo.emails.map((email) => ({ key: uniqueUUID(), email }));
    }
    return [];
  });
  const [phones, setPhones] = useState<{ key: string; phone: string }[]>(() => {
    const personInfo = getPersonInfo();
    if (personInfo) {
      return personInfo.phones.map((phone) => ({ key: uniqueUUID(), phone }));
    }
    return [];
  });
  const [urls, setUrls] = useState<{ key: string; url: string }[]>(() => {
    const personInfo = getPersonInfo();
    if (personInfo) {
      return personInfo.urls.map((url) => ({ key: uniqueUUID(), url }));
    }
    return [];
  });
  const collectedProfileUrls = useMemo(
    () => collectProfileUrls(urls.map((item) => item.url)),
    [urls]
  );

  function handleChange(list: AliasesDefinitions[]) {
    setFields(list);
  }
  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };
  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const updateField = (
    idx: number,
    field: "firstName" | "middleName" | "lastName",
    value: string
  ) => {
    fieldsList[idx][field] = value;
    fieldsList[idx].name_type = NameType.ALIAS;
    handleChange([...fieldsList]);
    updatePersonInfo("aliases", fieldsList);
  };

  function updatePersonInfo(
    field: keyof PersonInfoData,
    value:
      | string[]
      | { firstName: string; middleName: string; lastName: string }[]
      | string
  ) {
    const personInfo = getPersonInfo();
    if (personInfo) {
      setPersonInfo({
        ...personInfo,
        [field]: value,
      });
    }
  }

  function handleEmailsChange(emails: { key: string; email: string }[]) {
    setEmails(emails);
    updatePersonInfo(
      "emails",
      emails.map((email) => email.email)
    );
  }

  function handlePhonesChange(phones: { key: string; phone: string }[]) {
    setPhones(phones);
    updatePersonInfo(
      "phones",
      phones.map((phone) => phone.phone)
    );
  }

  function handleUrlsChange(urls: { key: string; url: string }[]) {
    setUrls(urls);
    updatePersonInfo(
      "urls",
      urls.map((url) => url.url)
    );
  }

  const hasError = (field: string, idx: number) => {
    if (field === "firstName") {
      return fieldsList[idx].firstName.length === 0;
    }
    if (field === "middleName") {
      return fieldsList[idx].middleName.length === 0;
    }
    if (field === "lastName") {
      return fieldsList[idx].lastName.length === 0;
    }
    return false;
  };

  const hasAliasFullNameError = (idx: number) => {
    const alias = fieldsList[idx];
    return !isWizardNameWithinDbLimit({
      firstName: alias.firstName,
      middleName: alias.middleName,
      lastName: alias.lastName,
    });
  };

  const isBirthDateValid = () => {
    if (birthDate.length === 0) {
      return true;
    }
    if (!validator.isDate(birthDate)) {
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    setIsDirty(REPORT_WIZARD_STEPS.PERSON_INFO, true);
    setIsDirty(REPORT_WIZARD_STEPS.BASIC_INFO, true);
    // Flush local state before leaving step; per-keystroke updates can race batching and stale urls on Summary PATCH.
    const personInfo = getPersonInfo();
    if (personInfo) {
      setPersonInfo({
        ...personInfo,
        urls: collectedProfileUrls.urls,
        emails: emails.map((e) => e.email.trim()).filter((e) => e.length > 0),
        phones: phones.map((p) => p.phone.trim()).filter((p) => p.length > 0),
        aliases: fieldsList.map((alias) => ({
          firstName: alias.firstName,
          middleName: alias.middleName,
          lastName: alias.lastName,
          name_type: alias.name_type,
        })),
        dateOfBirth: birthDate,
      });
    }
    if (steps[REPORT_WIZARD_STEPS.BASIC_INFO].isValid && isValid) {
      nextStep(isValid, true);
    }
  };

  const basicInfoStep = steps[REPORT_WIZARD_STEPS.BASIC_INFO];

  useEffect(() => {
    let emailErrors = false;
    let urlErrors = false;
    let birthDateErrors = false;
    let aliasErrors = false;
    let phoneErrors = false;
    if (
      emails.length === 0 ||
      (emails.length === 1 && emails[0].email.length === 0)
    ) {
      emailErrors = true;
    } else if (emails.length > 0) {
      emailErrors = emails.some(
        (email, idx) => !isValidEmailValidationFunction(email, idx)
      );
    }

    urlErrors = urls.some(
      (url, idx) =>
        !isValidUrlValidationFunction(url, idx) ||
        collectedProfileUrls.duplicateKeys.has(cleanedProfileUrl(url.url))
    );

    birthDateErrors = !isBirthDateValid();

    phoneErrors = phones.some(
      (phone, idx) => !isValidPhoneValidationFunction(phone, idx)
    );

    aliasErrors = fieldsList.some(
      (field, idx) =>
        hasError("firstName", idx) ||
        hasError("lastName", idx) ||
        hasAliasFullNameError(idx)
    );

    const hasErrors =
      emailErrors || urlErrors || birthDateErrors || aliasErrors || phoneErrors;
    setIsValid(basicInfoStep.isValid && !hasErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emails,
    urls,
    collectedProfileUrls,
    birthDate,
    fieldsList,
    phones,
    basicInfoStep,
  ]);

  const parseSignedUrl = useCallback((signedUrl: string) => {
    const url = new URL(signedUrl);
    const hostnameChunks = url.hostname.split(".");
    let path = url.pathname;
    if (path.startsWith("/")) {
      path = path.substring(1);
    }
    const pathChunks = path.split("/");
    let bucket = "";
    let key = "";
    if (hostnameChunks.length > 3 && hostnameChunks[1] === "s3") {
      // ex: https://{bucket}.s3.{region}.amazonaws.com/{key}
      bucket = hostnameChunks[0];
      key = decodeURIComponent(path);
    } else if (
      hostnameChunks.length > 2 &&
      hostnameChunks[0] === "s3" &&
      hostnameChunks[1] === "amazonaws"
    ) {
      // ex: https://s3.amazonaws.com/{bucket}/{key}
      bucket = pathChunks[0];
      key = decodeURIComponent(pathChunks.slice(1).join("/"));
    } else {
      throw new Error("Could not parse bucket and key from signed url");
    }
    return { bucket, key };
  }, []);

  const handleResumeFile = useCallback(
    async (file: File): Promise<boolean> => {
      const person: string = (await ensurePersonAvailable()) ?? "";
      if (person === "") {
        return false;
      }
      const personDocumentUploadUrl = await getPersonDocumentUploadUrl({
        person_uuid: person,
        document_type: "resume",
        filename: file.name,
      });

      await uploadFileToS3({
        uploadUrl: personDocumentUploadUrl.upload_url,
        file: file,
      });
      const parsedUrl = parseSignedUrl(personDocumentUploadUrl.upload_url);
      const resume: Resume = await parseResumeFromS3({
        id: person,
        bucket: parsedUrl.bucket ?? "",
        key: parsedUrl.key ?? "",
      });
      const newSteps = resumeToSteps(resume);
      let info = initializeFieldsCallback(
        newSteps[REPORT_WIZARD_STEPS.PERSON_INFO].data as PersonInfoData
      );
      setFields(info.aliases);
      setEmails(info.emails);
      setPhones(info.phones);
      setUrls(info.urls);
      return true;
    },
    [
      ensurePersonAvailable,
      getPersonDocumentUploadUrl,
      initializeFieldsCallback,
      parseResumeFromS3,
      parseSignedUrl,
      resumeToSteps,
      uploadFileToS3,
    ]
  );

  useEffect(() => {
    const parseTempFile = async () => {
      let success = false;
      let error = false;
      if (!!tempFile && !isParsingResume) {
        try {
          setIsParsingResume(true);
          success = await handleResumeFile(tempFile);
        } catch (e) {
          error = true;
        } finally {
          if (success || error) {
            setTempFile(null);
            setIsParsingResume(false);
          }
        }
      }
    };
    parseTempFile();
  }, [
    tempFile,
    isParsingResume,
    handleResumeFile,
    setTempFile,
    setIsParsingResume,
  ]);

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const { files } = event.target as HTMLInputElement;
      if (files && files.length > 0 && !isParsingResume && !tempFile) {
        setTempFile(files[0]);
      }
    } catch (e) {
      setTempFile(null);
      setIsParsingResume(false);
    }
  };
  const isDirty = getIsDirty(REPORT_WIZARD_STEPS.PERSON_INFO);

  return (
    <div>
      <div className="flex justify-between items-center">
        <span className="text-lg font-bold text-darkBlue">
          Person Information
        </span>
        {showResumeParse && (
          <Button
            disabled={isParsingResume || anyResumeError}
            type={ButtonTypes.default}
            text={
              isParsingResume
                ? "Parsing..."
                : anyResumeError
                ? "Error with Resume"
                : "Parse Resume"
            }
            colorType={
              anyResumeError
                ? ButtonColorTypes.secondary
                : ButtonColorTypes.alternate
            }
            onClick={() =>
              ensurePersonAvailable() &&
              fileInput &&
              fileInput.current &&
              fileInput.current.click()
            }
          />
        )}
        <input
          className="hidden"
          type="file"
          ref={fileInput}
          name="_file"
          onChange={changeHandler}
        />
      </div>
      <div className="gap-2 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        <div>
          <div className="flex-1 pb-4">Aliases</div>
          {fieldsList.length > 0 && (
            <div className="flex-1 mr-4 flex flex-col gap-2">
              {fieldsList.map((fields, idx) => (
                <div key={fields.key} className="mb-2">
                  <div className="flex flex-nowrap items-start gap-4">
                    <div className="flex-1">
                      <InputField
                        handleChange={(e) => {
                          updateField(
                            idx,
                            "firstName",
                            sanitizeAlias(e.target.value)
                          );
                        }}
                        name={`first-name-${idx}`}
                        placeholder=""
                        label={"First Name*"}
                        classes="flex-1"
                        value={fields.firstName}
                        className={classNames(
                          isDirty && hasError("firstName", idx)
                            ? "border-rose-600"
                            : "",
                          "w-full"
                        )}
                      />
                      {isDirty && hasError("firstName", idx) && (
                        <div className="text-red-500 text-sm mt-2">
                          First name is required
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <InputField
                        handleChange={(e) => {
                          updateField(
                            idx,
                            "middleName",
                            sanitizeAlias(e.target.value)
                          );
                        }}
                        name={`middle-name-${idx}`}
                        placeholder=""
                        label={"Middle Name"}
                        classes="border-rose-600 flex-1"
                        value={fields.middleName}
                        className={"w-full"}
                      />
                    </div>

                    <div className="flex-1">
                      <InputField
                        handleChange={(e) => {
                          updateField(
                            idx,
                            "lastName",
                            sanitizeAlias(e.target.value)
                          );
                        }}
                        classes="flex-1"
                        name={`last-name-${idx}`}
                        placeholder=""
                        label={"Last Name *"}
                        value={fields.lastName}
                        className={classNames(
                          isDirty && hasError("lastName", idx)
                            ? "border-rose-600"
                            : "",
                          "w-full"
                        )}
                      />
                      {isDirty && hasError("lastName", idx) && (
                        <div className="text-red-500 text-sm mt-2">
                          Last name is required
                        </div>
                      )}
                    </div>
                    <span className="pl-2 h-0 w-10 pt-8">
                      <div
                        className="rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
                        onClick={(e) => handleRemove(e, idx)}
                      >
                        <XIcon />
                      </div>
                    </span>
                  </div>
                  {isDirty && hasAliasFullNameError(idx) && (
                    <div className="text-red-500 text-sm mt-2">
                      Combined full name must not exceed 255 characters
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-4">
            <AddButton text="Add Aliases" onClick={handleAdd} />
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <div className="flex-1">
            <MultiAdd<{ key: string; email: string }>
              fieldKey="email"
              buttonText="Add Email"
              label="Email*"
              buildEmptyDefinition={() => ({ key: uniqueUUID(), email: "" })}
              onChange={handleEmailsChange}
              prefill={emails}
              isValidItemFunction={isValidEmailValidationFunction}
              isDirty={isDirty}
              invalidMessage="Please enter a valid e-mail address"
            />
          </div>
          <div className="flex-1">
            <MultiAdd<{ key: string; phone: string }>
              fieldKey="phone"
              buttonText="Add Phone Number"
              label="Phone Number"
              buildEmptyDefinition={() => ({ key: uniqueUUID(), phone: "" })}
              onChange={handlePhonesChange}
              prefill={phones}
              isDirty={isDirty}
              isValidItemFunction={isValidPhoneValidationFunction}
              invalidMessage="Please enter a valid phone number"
              inputType="text"
            />
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <div className="flex-1">
            <InputField
              value={birthDate}
              handleChange={(e) => {
                setBirthDate(e.target.value);
                updatePersonInfo("dateOfBirth", e.target.value);
              }}
              onBlur={(e) => {
                if (!isBirthDateValid()) {
                  e.target.value = "";
                }
              }}
              name={"birth-date"}
              placeholder=""
              label={"Date of Birth"}
              type="date"
              max={new Date().toISOString().split("T")[0]}
              className={classNames(
                isDirty && !isBirthDateValid() ? "border-rose-600" : "",
                "w-full"
              )}
            />
          </div>
          <div className="flex-1"></div>
        </div>
      </div>
      <div className="text-lg font-bold text-darkBlue pt-8">
        Social Networks
      </div>
      <div className="gap-2 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        <MultiAdd<{ key: string; url: string }>
          fieldKey="url"
          buttonText="Add Profile"
          label="Profile URL"
          buildEmptyDefinition={() => ({ key: uniqueUUID(), url: "" })}
          onChange={handleUrlsChange}
          prefill={urls}
          isDirty={isDirty}
          invalidMessage="Invalid URL (max 255 characters)"
          invalidMessageForItem={(item, idx) =>
            collectedProfileUrls.duplicateKeys.has(cleanedProfileUrl(item.url))
              ? PROFILE_URL_ERROR.duplicate
              : profileUrlErrorMessage(item.url, { allowEmpty: idx === 0 }) ??
                "Invalid URL (max 255 characters)"
          }
          alwaysShowInvalid
          inputType="text"
          isValidItemFunction={(item, idx) =>
            isValidUrlValidationFunction(item, idx) &&
            !collectedProfileUrls.duplicateKeys.has(cleanedProfileUrl(item.url))
          }
        />
      </div>
      <div className="flex justify-end mt-8">
        <Button
          text={"Next Step"}
          colorType={ButtonColorTypes.custom}
          isRoundedFull={true}
          className={classNames(
            "bg-orange-500 text-white rounded-full ml-auto w-56"
          )}
          onClick={handleNextStep}
          disabled={isDirty && !isValid}
        />
      </div>
    </div>
  );
};

function MultiAdd<T extends { key: string }>({
  onChange,
  prefill,
  buildEmptyDefinition,
  fieldKey,
  buttonText,
  label,
  isValidItemFunction,
  isDirty,
  invalidMessage,
  invalidMessageForItem,
  alwaysShowInvalid,
  inputType = "email",
}: {
  onChange?: (emails: T[]) => void;
  prefill?: T[];
  alternateColor?: boolean;
  buildEmptyDefinition: () => T;
  fieldKey: string & keyof T;
  buttonText: string;
  label: string;
  isValidItemFunction?: (item: T, idx: number) => boolean;
  isDirty: boolean;
  invalidMessage?: string;
  invalidMessageForItem?: (item: T, idx: number) => string;
  alwaysShowInvalid?: boolean;
  inputType?: string;
}) {
  const [fieldsList, setFields] = useState<T[]>(() => {
    if (prefill && prefill.length > 0) {
      return prefill;
    }
    return [buildEmptyDefinition()];
  });
  useEffect(() => {
    if (prefill && prefill.length > 0) {
      setFields(prefill);
    }
  }, [prefill]);

  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (idx: number, field: string, value: string) => {
    fieldsList[idx][field as keyof T] = value as T[keyof T];
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: T[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (idx: number) => {
    removeField(idx);
  };
  const tFieldKey = fieldKey as keyof T;
  return (
    <div className="flex flex-col">
      <div className="flex-1">
        {fieldsList.map((fields, idx) => (
          <div
            key={idx}
            className={classNames(
              "flex flex-col flex-wrap items-center",
              idx > 0 ? "mt-4" : ""
            )}
          >
            <div className="flex-1 flex items-center mr-4 w-full">
              <InputField
                handleChange={(e) => {
                  updateField(idx, fieldKey, e.target.value);
                }}
                name={`${fieldKey}-${idx}`}
                placeholder=""
                value={fieldsList[idx][tFieldKey] as string}
                label={label}
                type={inputType}
                classes="flex-1 h-[70px]"
                className={classNames(
                  "w-full",
                  isValidItemFunction
                    ? !isValidItemFunction(fieldsList[idx], idx) &&
                      (isDirty || alwaysShowInvalid)
                      ? "border-rose-600"
                      : ""
                    : ""
                )}
              />

              <span className="pl-2 h-0 w-10">
                {idx > 0 && (
                  <div
                    className="rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
                    onClick={(e) => handleRemove(idx)}
                  >
                    <XIcon />
                  </div>
                )}
              </span>
            </div>
            {isValidItemFunction &&
              !isValidItemFunction(fieldsList[idx], idx) &&
              (isDirty || alwaysShowInvalid) && (
                <div className="text-red-500 text-sm w-full mt-2">
                  {invalidMessageForItem
                    ? invalidMessageForItem(fieldsList[idx], idx)
                    : invalidMessage}
                </div>
              )}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <AddButton text={buttonText} onClick={handleAdd} />
      </div>
    </div>
  );
}
